import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Table, TableBody, TableCell, TableHead, TableHeadCell, TableHeadRow, TableRow, Button } from '@client/toolkit';
import { SortableTableHeadCell } from '../../../../reporting/components/sortable-table-head-cell';
import { AgreementToInvoiceItem } from './mock-data';
import { SortDirection } from './invoice-overview-table.component';
import { MdEdit, MdPause } from 'react-icons/md';

interface AgreementsToInvoiceTableProps {
  data: AgreementToInvoiceItem[];
  sortColumn?: string;
  sortDirection?: SortDirection;
  onSort: (column: string, direction: SortDirection) => void;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currency: 'NOK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  return dayjs(dateString).format('DD.MM.YYYY');
};

export function AgreementsToInvoiceTable({ data, sortColumn, sortDirection, onSort }: AgreementsToInvoiceTableProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('agreements');

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      onSort(column, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(column, 'asc');
    }
  };

  return (
    <div className="px-4 pb-4">
      <Table className="w-full table-fixed" size="small">
        <TableHead>
          <TableHeadRow>
            <TableHeadCell className="w-[20%]">{t('reports.agreementsTable.tenant')}</TableHeadCell>
            <TableHeadCell className="w-[20%]">{t('reports.agreementsTable.agreementName')}</TableHeadCell>
            <SortableTableHeadCell
              id="periodFrom"
              label={t('reports.agreementsTable.billingPeriod')}
              className="w-[18%]"
              handleSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
            />
            <SortableTableHeadCell
              id="amount"
              label={t('reports.agreementsTable.amount')}
              className="w-[13%]"
              handleSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
            />
            <TableHeadCell className="w-[14%]">{t('reports.agreementsTable.status')}</TableHeadCell>
            <TableHeadCell className="w-[15%]">{t('reports.agreementsTable.actions')}</TableHeadCell>
          </TableHeadRow>
        </TableHead>
        <TableBody>
          {data.map((item) => (
            <TableRow key={item.id} className="[&>td]:align-middle">
              <TableCell className="w-[20%]">
                <span className="font-medium text-white">{item.tenantName}</span>
              </TableCell>
              <TableCell className="w-[20%]">
                <button
                  type="button"
                  className="text-brand-primary hover:underline text-left"
                  onClick={() => navigate(`/agreements/${item.agreementId}`)}
                >
                  {item.agreementName}
                </button>
              </TableCell>
              <TableCell className="w-[18%]">
                <span className="text-white">
                  {formatDate(item.periodFrom)} - {formatDate(item.periodTo)}
                </span>
              </TableCell>
              <TableCell className="w-[13%]">
                <span className="text-white">{formatCurrency(item.amount)}</span>
              </TableCell>
              <TableCell className="w-[14%]">
                <span className="text-green-400">{t('reports.agreementsTable.readyForInvoicing')}</span>
              </TableCell>
              <TableCell className="w-[15%]">
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    color="primary-light"
                    onClick={() => navigate(`/agreements/${item.agreementId}`)}
                    className="text-white font-normal text-xs"
                    startIcon={<MdEdit className="text-brand-primary" />}
                  >
                    {t('reports.agreementsTable.adjust')}
                  </Button>
                  <Button
                    size="sm"
                    color="primary-light"
                    onClick={() => {}}
                    className="text-white font-normal text-xs opacity-50 cursor-not-allowed"
                    disabled
                    startIcon={<MdPause className="text-yellow-400" />}
                  >
                    {t('reports.agreementsTable.hold')}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
